// ----------------------------------------------------------------------

// WHY THE `&&` SELECTORS BELOW (fix, 2026-07-30)
//
// Every `variant="contained"` button in the app was rendering with NO
// background — white `contrastText` on a white card, i.e. invisible.
//
// Tailwind's preflight (src/output.css) resets buttons with:
//     button, [type='button'], [type='reset'], [type='submit'] {
//       background-color: transparent;
//     }
// `[type='button']` is an ATTRIBUTE selector, specificity (0,1,0) — exactly the
// same as MUI's generated emotion class. MUI renders `<button type="button">`,
// so both rules match with equal specificity and the tie is broken by source
// order. `StyledEngineProvider injectFirst` (theme/index.js) puts MUI's styles
// FIRST, and output.css is imported after it in index.js, so Tailwind wins and
// the background is wiped.
//
// `&&` makes emotion emit the class twice (`.css-x.css-x`, specificity 0,2,0),
// which beats the attribute selector regardless of order. That is the whole fix.
//
// The alternative — disabling Tailwind preflight — is arguably more correct,
// since two competing CSS resets is the underlying problem and MUI's CssBaseline
// already does that job. It was not taken here because preflight also normalises
// headings, lists and images that Tailwind-styled markup elsewhere may rely on,
// and that could not be verified without a browser pass. Worth revisiting.
//
// SYMPTOM TO WATCH FOR: if contained buttons ever go invisible again, check
// whether a new global stylesheet is loaded after MUI and matches `[type=...]`.

export default function Button(theme) {
  // Contained buttons carry their own background; this restores it at a
  // specificity preflight cannot tie with.
  const contained = (color) => ({
    '&&': {
      backgroundColor: theme.palette[color].main,
      color: theme.palette[color].contrastText,
      '&:hover': { backgroundColor: theme.palette[color].dark },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled
      }
    }
  });

  return {
    MuiButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            boxShadow: 'none'
          }
        },
        sizeLarge: {
          height: 48
        },
        containedInherit: {
          color: theme.palette.grey[800],
          boxShadow: theme.customShadows.z8,
          // Same reasoning as the colour variants, using the greys MUI's
          // `inherit` contained button expects.
          '&&': {
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.grey[800]
          },
          '&:hover': {
            backgroundColor: theme.palette.grey[400]
          }
        },
        containedPrimary: {
          boxShadow: theme.customShadows.primary,
          ...contained('primary')
        },
        containedSecondary: {
          boxShadow: theme.customShadows.secondary,
          ...contained('secondary')
        },
        containedSuccess: contained('success'),
        containedError: contained('error'),
        containedWarning: contained('warning'),
        containedInfo: contained('info'),
        outlinedInherit: {
          border: `1px solid ${theme.palette.grey[500_32]}`,
          '&:hover': {
            backgroundColor: theme.palette.action.hover
          }
        },
        textInherit: {
          '&:hover': {
            backgroundColor: theme.palette.action.hover
          }
        }
      }
    }
  };
}
